import axios from 'axios';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { getCanonicalFormDescription } from '../pages/stock/secFormDescriptions';

const normaliseFormType = (value) => String(value || '').trim().toUpperCase();

const safeParseDate = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const normaliseSections = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string') {
    return value.split(/[,;]+/).flatMap((item) => {
      const trimmed = item.trim();
      return trimmed ? [trimmed] : [];
    });
  }

  return [];
};

const normaliseFiling = (filing) => {
  if (!filing || typeof filing !== 'object') {
    return null;
  }

  const formType =
    filing.formType ||
    filing.form_type ||
    filing.form ||
    filing.type ||
    filing.document_type ||
    '—';

  const description =
    filing.description ||
    filing.formDescription ||
    filing.form_description ||
    filing.primary_doc_description ||
    filing.primaryDocumentDescription ||
    filing.primary_document_description ||
    '';

  const filingDateRaw =
    filing.filingDate ||
    filing.filing_date ||
    filing.filedAt ||
    filing.filed_at ||
    filing.acceptanceDateTime ||
    filing.acceptance_datetime ||
    filing.filingDateTime ||
    filing.filing_datetime ||
    '';

  const filingDate = filingDateRaw ? String(filingDateRaw).split('T')[0] : '';

  const reportDateRaw =
    filing.reportingDate ||
    filing.reporting_date ||
    filing.reportDate ||
    filing.report_date ||
    filing.periodOfReport ||
    filing.period_of_report ||
    '';

  const reportingDate = reportDateRaw ? String(reportDateRaw).split('T')[0] : '';

  const href =
    filing.url ||
    filing.href ||
    filing.filingUrl ||
    filing.filing_url ||
    filing.documentUrl ||
    filing.document_url ||
    filing.link ||
    null;

  const sections = normaliseSections(
    filing.sections || filing.items || filing.item || filing.topics
  );

  const aiRaw = filing.ai || filing.ai_enrichment || filing.enrichment || null;
  const masterTagsRaw = aiRaw?.master_tags ?? aiRaw?.masterTags;
  const masterTags = Array.isArray(masterTagsRaw)
    ? masterTagsRaw.flatMap((tag) => {
        const trimmed = String(tag || '').trim();
        return trimmed ? [trimmed] : [];
      })
    : [];
  const aiTagsRaw = aiRaw?.tags;
  const aiTags = Array.isArray(aiTagsRaw)
    ? aiTagsRaw.flatMap((tag) => {
        const trimmed = String(tag || '').trim();
        return trimmed ? [trimmed] : [];
      })
    : [];
  const aiSummary = aiRaw?.summary140 ? String(aiRaw.summary140) : '';
  const aiStatus = aiRaw?.status ? String(aiRaw.status) : null;
  const form4Raw = filing.form4 || filing.form4_enrichment || null;
  const form4 = form4Raw && typeof form4Raw === 'object'
    ? form4Raw
    : null;
  const accessionNumber =
    filing.accessionNumber ||
    filing.accession_number ||
    filing.documentId ||
    filing.document_id ||
    null;

  return {
    id:
      filing.id ||
      accessionNumber ||
      `${formType}-${filingDate}-${reportingDate}`,
    accessionNumber,
    cik: filing.cik || null,
    formType,
    formTypeKey: normaliseFormType(formType),
    description: description || getCanonicalFormDescription(normaliseFormType(formType)),
    filingDate,
    filingDateValue: safeParseDate(filingDate),
    reportingDate,
    reportingDateValue: safeParseDate(reportingDate),
    href,
    sections,
    ai: {
      status: aiStatus,
      masterTags,
      tags: aiTags,
      summary140: aiSummary,
    },
    form4,
  };
};

export const normaliseFilingsPayload = (data) => {
  const filingsPayload = Array.isArray(data)
    ? data
    : data?.filings || data?.results || data?.data || [];

  return filingsPayload.flatMap((filing) => {
    const normalised = normaliseFiling(filing);
    return normalised ? [normalised] : [];
  });
};

const aiTagsKey = (tags) =>
  Array.isArray(tags)
    ? tags
        .flatMap((tag) => {
          const trimmed = String(tag || '').trim();
          return trimmed ? [trimmed] : [];
        })
        .join('|')
    : '';

const isAiEqual = (a, b) => {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    (a.status || null) === (b.status || null) &&
    (a.summary140 || '') === (b.summary140 || '') &&
    aiTagsKey(a.masterTags) === aiTagsKey(b.masterTags) &&
    aiTagsKey(a.tags) === aiTagsKey(b.tags)
  );
};

const SEC_FILINGS_PAGE_SIZE = 100;
const SEC_FILINGS_LOAD_ALL_PAGE_SIZE = 250;

async function fetchSecFilingsPage(
  apiBaseUrl,
  ticker,
  offset,
  signal,
  entityCik = null,
  limit = SEC_FILINGS_PAGE_SIZE
) {
  if (signal?.aborted) {
    return null;
  }

  const resourcePath = entityCik
    ? `/api/pre-ipo/entities/${encodeURIComponent(entityCik)}/filings`
    : `/api/sec_filings?ticker=${encodeURIComponent(ticker)}`;
  const separator = resourcePath.includes('?') ? '&' : '?';
  const endpoint = `${apiBaseUrl}${resourcePath}${separator}`
    + `include_ai=1&include_form4=1&form4_budget=5&limit=${limit}`
    + `&offset=${Math.max(Number(offset) || 0, 0)}`;
  const { data } = await axios.get(endpoint, { signal });
  const filings = normaliseFilingsPayload(data);
  return {
    filings,
    totalCount: Number(data?.count) || filings.length,
    hasMore: Boolean(data?.has_more),
    nextOffset: Number(data?.next_offset) || filings.length,
  };
}

const form4SummaryKey = (form4) => {
  const summary = form4?.row_summary || {};
  return [
    form4?.status || '',
    form4?.details_loaded ? '1' : '0',
    summary.owner_name || '',
    summary.title || '',
    summary.action || '',
    summary.shares || '',
    summary.total_value || '',
    summary.transaction_count || '',
  ].join('|');
};

const isForm4Equal = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return form4SummaryKey(a) === form4SummaryKey(b) && (a.error || '') === (b.error || '');
};

function mergeFilingsWithEnrichmentUpdates(prevFilings, normalised) {
  const latestById = new Map(normalised.map((filing) => [filing.id, filing]));
  return prevFilings.map((existing) => {
    const next = latestById.get(existing.id);
    if (!next) return existing;

    const updates = {};
    if (next.ai !== undefined && !isAiEqual(existing.ai, next.ai)) {
      updates.ai = next.ai;
    }
    if (
      next.form4 &&
      !(existing.form4?.status === 'complete' && next.form4.status === 'pending') &&
      !(existing.form4?.details_loaded && !next.form4.details_loaded) &&
      !isForm4Equal(existing.form4, next.form4)
    ) {
      updates.form4 = next.form4;
    }
    return Object.keys(updates).length ? { ...existing, ...updates } : existing;
  });
}

async function fetchEnrichmentUpdates(apiBaseUrl, accessionNumbers, signal) {
  const endpoint = `${apiBaseUrl}/api/sec_filings/enrichments`;
  const { data } = await axios.post(
    endpoint,
    { accession_numbers: accessionNumbers },
    { signal }
  );
  return (data?.updates || []).map((update) => {
    const normalised = normaliseFiling({
      accession_number: update.accession_number,
      ai: update.ai,
      form4: update.form4,
    });
    if (!normalised) return null;
    if (!update.ai) delete normalised.ai;
    if (!update.form4) delete normalised.form4;
    return normalised;
  }).filter(Boolean);
}

function startSecFilingsEnrichmentPolling(apiBaseUrl, accessionNumbers, onUpdate) {
  let isCancelled = false;
  const controller = new AbortController();

  const refreshEnrichments = () => {
    if (isCancelled || controller.signal.aborted) return;

    fetchEnrichmentUpdates(apiBaseUrl, accessionNumbers, controller.signal)
      .then((normalised) => {
        if (isCancelled) return;
        onUpdate(normalised);
      })
      .catch((err) => {
        if (axios.isCancel(err) || err?.name === 'CanceledError') return;
      });
  };

  refreshEnrichments();
  const interval = setInterval(refreshEnrichments, 4000);

  return () => {
    isCancelled = true;
    controller.abort();
    clearInterval(interval);
  };
}

function startSecFilingsFetch(apiBaseUrl, ticker, onSuccess, onError, entityCik = null) {
  let isCancelled = false;
  const controller = new AbortController();

  fetchSecFilingsPage(apiBaseUrl, ticker, 0, controller.signal, entityCik)
    .then((page) => {
      if (isCancelled) return;
      onSuccess(page);
    })
    .catch((err) => {
      if (axios.isCancel(err) || err?.name === 'CanceledError') return;

      if (!isCancelled && !controller.signal.aborted) {
        onError(
          err?.response?.data?.error ||
            err?.message ||
            'Unable to load SEC filings right now.'
        );
      }
    });

  return () => {
    isCancelled = true;
    controller.abort();
  };
}

const SEC_FILINGS_INITIAL_STATE = {
  filings: [],
  isFetching: true,
  isLoadingAll: false,
  isLoadingMore: false,
  error: null,
  totalCount: 0,
  hasMore: false,
  nextOffset: 0,
};

function secFilingsReducer(state, action) {
  switch (action.type) {
    case 'fetch_start':
      return { ...SEC_FILINGS_INITIAL_STATE };
    case 'fetch_success':
      return {
        filings: action.page.filings,
        isFetching: false,
        isLoadingAll: false,
        isLoadingMore: false,
        error: null,
        totalCount: action.page.totalCount,
        hasMore: action.page.hasMore,
        nextOffset: action.page.nextOffset,
      };
    case 'fetch_error':
      return {
        filings: [],
        isFetching: false,
        isLoadingAll: false,
        isLoadingMore: false,
        error: action.error,
        totalCount: 0,
        hasMore: false,
        nextOffset: 0,
      };
    case 'load_more_start':
      return { ...state, isLoadingMore: true };
    case 'load_all_start':
      return { ...state, isLoadingAll: true, isLoadingMore: true };
    case 'load_more_success': {
      const existingIds = new Set(state.filings.map((filing) => filing.id));
      const additions = action.page.filings.filter((filing) => !existingIds.has(filing.id));
      return {
        ...state,
        filings: [...state.filings, ...additions],
        isLoadingMore: false,
        totalCount: action.page.totalCount,
        hasMore: action.page.hasMore,
        nextOffset: action.page.nextOffset,
      };
    }
    case 'load_more_error':
      return {
        ...state,
        isLoadingAll: false,
        isLoadingMore: false,
        error: action.error,
      };
    case 'load_all_progress': {
      const existingIds = new Set(state.filings.map((filing) => filing.id));
      const additions = action.page.filings.filter((filing) => !existingIds.has(filing.id));
      return {
        ...state,
        filings: [...state.filings, ...additions],
        totalCount: action.page.totalCount,
        hasMore: action.page.hasMore,
        nextOffset: action.page.nextOffset,
      };
    }
    case 'load_all_complete':
      return { ...state, isLoadingAll: false, isLoadingMore: false };
    case 'merge_ai_updates':
      return {
        ...state,
        filings: mergeFilingsWithEnrichmentUpdates(state.filings, action.normalised),
      };
    case 'form4_enrich_start':
      return {
        ...state,
        filings: state.filings.map((filing) =>
          filing.id === action.filingId
            ? {
                ...filing,
                form4: {
                  ...(filing.form4 || {}),
                  details_loading: true,
                  details_error: null,
                },
              }
            : filing
        ),
      };
    case 'form4_enrich_success':
      return {
        ...state,
        filings: state.filings.map((filing) =>
          filing.id === action.filingId
            ? { ...filing, form4: action.form4 }
            : filing
        ),
      };
    case 'form4_enrich_error':
      return {
        ...state,
        filings: state.filings.map((filing) =>
          filing.id === action.filingId
            ? {
                ...filing,
                form4: {
                  ...(filing.form4 || {}),
                  details_loading: false,
                  details_error: action.error,
                },
              }
            : filing
        ),
      };
    default:
      return state;
  }
}

export function useSecFilingsData(ticker, apiBaseUrl, { entityCik = null } = {}) {
  const [{
    error,
    filings,
    hasMore,
    isFetching,
    isLoadingAll,
    isLoadingMore,
    nextOffset,
    totalCount,
  }, dispatch] = useReducer(
    secFilingsReducer,
    SEC_FILINGS_INITIAL_STATE
  );
  const loadMoreControllerRef = useRef(null);

  useEffect(() => {
    if (!ticker && !entityCik) {
      return undefined;
    }

    loadMoreControllerRef.current?.abort();
    dispatch({ type: 'fetch_start' });
    const cancelInitialFetch = startSecFilingsFetch(
      apiBaseUrl,
      ticker,
      (page) => {
        dispatch({ type: 'fetch_success', page });
      },
      (errorMessage) => {
        dispatch({ type: 'fetch_error', error: errorMessage });
      },
      entityCik
    );
    return () => {
      cancelInitialFetch();
      loadMoreControllerRef.current?.abort();
    };
  }, [apiBaseUrl, entityCik, ticker]);

  const pendingAccessions = useMemo(
    () => filings.flatMap((filing) => (
      filing?.accessionNumber && (
        filing?.ai?.status === 'pending' || filing?.form4?.status === 'pending'
      )
        ? [filing.accessionNumber]
        : []
    )),
    [filings]
  );
  const pendingAccessionsKey = pendingAccessions.join('|');

  useEffect(() => {
    if (!pendingAccessionsKey) {
      return undefined;
    }

    return startSecFilingsEnrichmentPolling(apiBaseUrl, pendingAccessions, (normalised) => {
      dispatch({ type: 'merge_ai_updates', normalised });
    });
  }, [apiBaseUrl, pendingAccessions, pendingAccessionsKey]);

  const loadMore = useCallback(async () => {
    if ((!ticker && !entityCik) || !hasMore || isLoadingMore) return;
    loadMoreControllerRef.current?.abort();
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    dispatch({ type: 'load_more_start' });
    try {
      const page = await fetchSecFilingsPage(
        apiBaseUrl,
        ticker,
        nextOffset,
        controller.signal,
        entityCik
      );
      if (controller.signal.aborted || !page) return;
      dispatch({ type: 'load_more_success', page });
    } catch (err) {
      if (
        controller.signal.aborted ||
        axios.isCancel(err) ||
        err?.name === 'CanceledError'
      ) {
        return;
      }
      dispatch({
        type: 'load_more_error',
        error: err?.response?.data?.error || err?.message || 'Unable to load more filings.',
      });
    } finally {
      if (loadMoreControllerRef.current === controller) {
        loadMoreControllerRef.current = null;
      }
    }
  }, [apiBaseUrl, entityCik, hasMore, isLoadingMore, nextOffset, ticker]);

  const loadAll = useCallback(async () => {
    if ((!ticker && !entityCik) || !hasMore || isLoadingMore) return;
    loadMoreControllerRef.current?.abort();
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    dispatch({ type: 'load_all_start' });
    try {
      let offset = nextOffset;
      let morePagesRemain = true;
      while (morePagesRemain && !controller.signal.aborted) {
        const page = await fetchSecFilingsPage(
          apiBaseUrl,
          ticker,
          offset,
          controller.signal,
          entityCik,
          SEC_FILINGS_LOAD_ALL_PAGE_SIZE
        );
        if (controller.signal.aborted || !page) return;
        dispatch({ type: 'load_all_progress', page });
        morePagesRemain = page.hasMore;
        if (morePagesRemain && page.nextOffset <= offset) {
          throw new Error('Unable to continue loading all filings.');
        }
        offset = page.nextOffset;
      }
      dispatch({ type: 'load_all_complete' });
    } catch (err) {
      if (
        controller.signal.aborted ||
        axios.isCancel(err) ||
        err?.name === 'CanceledError'
      ) {
        return;
      }
      dispatch({
        type: 'load_more_error',
        error: err?.response?.data?.error || err?.message || 'Unable to load all filings.',
      });
    } finally {
      if (loadMoreControllerRef.current === controller) {
        loadMoreControllerRef.current = null;
      }
    }
  }, [apiBaseUrl, entityCik, hasMore, isLoadingMore, nextOffset, ticker]);

  const enrichForm4 = useCallback(async (filing) => {
    const hasEmbeddedDetails = Boolean(
      Array.isArray(filing?.form4?.transactions) ||
      Array.isArray(filing?.form4?.holdings) ||
      Array.isArray(filing?.form4?.reporting_owners)
    );
    if (!filing || filing.form4?.details_loaded || hasEmbeddedDetails) {
      return filing?.form4 || null;
    }

    dispatch({ type: 'form4_enrich_start', filingId: filing.id });
    try {
      let data;
      if (filing.form4?.status === 'complete') {
        const endpoint = `${apiBaseUrl}/api/sec_filings/form4/${encodeURIComponent(
          filing.accessionNumber || filing.id
        )}`;
        ({ data } = await axios.get(endpoint));
      } else {
        const endpoint = `${apiBaseUrl}/api/sec_filings/enrich_form4`;
        ({ data } = await axios.post(endpoint, {
          accession_number: filing.accessionNumber || filing.id,
          form_type: filing.formType,
          url: filing.href,
          cik: filing.cik,
        }));
      }
      const form4 = data?.form4 || null;
      if (!form4) {
        throw new Error('The Form 4 response was empty.');
      }
      const detailedForm4 = {
        ...form4,
        details_loaded: true,
        details_loading: false,
        details_error: null,
      };
      dispatch({ type: 'form4_enrich_success', filingId: filing.id, form4: detailedForm4 });
      return detailedForm4;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error || err?.message || 'Unable to load Form 4 details.';
      dispatch({
        type: 'form4_enrich_error',
        filingId: filing.id,
        error: errorMessage,
      });
      return null;
    }
  }, [apiBaseUrl]);

  return {
    enrichForm4,
    error,
    filings,
    hasMore,
    isFetching,
    isLoadingAll,
    isLoadingMore,
    loadAll,
    loadMore,
    totalCount,
  };
}
